// apps/web/src/views/app-views/roulette/daily-limits/index.js

import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'

import PrizeConfigurationsService from 'services/PrizeConfigurationsService'

const normalizeDateOnly = (value) => {
  if (!value) {
    return null
  }

  return String(value).substring(0, 10)
}

const { Title, Text } = Typography

const DailyLimitsPage = () => {
  const [games, setGames] = useState([])
  const [gameId, setGameId] = useState()

  const [prizes, setPrizes] = useState([])
  const [prizeId, setPrizeId] = useState()

  const [configurations, setConfigurations] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingConfiguration, setEditingConfiguration] =
    useState(null)

  const [searchPeriod, setSearchPeriod] = useState(null)
  const [statusFilter, setStatusFilter] = useState()

  const [form] = Form.useForm()

  const selectedGame = useMemo(() => {
    return games.find((game) => game.id === gameId)
  }, [games, gameId])

  const selectedPrize = useMemo(() => {
    return prizes.find((prize) => prize.id === prizeId)
  }, [prizes, prizeId])

  const activeConfigurations = useMemo(() => {
    return configurations.filter(
      (configuration) => configuration.enabled,
    ).length
  }, [configurations])

  const inactiveConfigurations = useMemo(() => {
    return configurations.filter(
      (configuration) => !configuration.enabled,
    ).length
  }, [configurations])

  const totalConfiguredLimits = useMemo(() => {
    return configurations.reduce(
      (total, configuration) =>
        total + Number(configuration.dailyLimit || 0),
      0,
    )
  }, [configurations])

  const filteredConfigurations = useMemo(() => {
    return configurations.filter((configuration) => {
      if (
        statusFilter === 'ACTIVE' &&
        !configuration.enabled
      ) {
        return false
      }

      if (
        statusFilter === 'INACTIVE' &&
        configuration.enabled
      ) {
        return false
      }

      if (
        Array.isArray(searchPeriod) &&
        searchPeriod.length === 2
      ) {
        const configurationDate = dayjs(
          configuration.date,
        )

        const startDate = searchPeriod[0].startOf('day')
        const endDate = searchPeriod[1].endOf('day')

        if (
          configurationDate.isBefore(startDate) ||
          configurationDate.isAfter(endDate)
        ) {
          return false
        }
      }

      return true
    })
  }, [configurations, searchPeriod, statusFilter])

  const loadGames = async () => {
    try {
      const list =
        await PrizeConfigurationsService.getGames()

      setGames(list)

      if (list.length && !gameId) {
        setGameId(list[0].id)
      }
    } catch (error) {
      console.error('ERRO AO CARREGAR JOGOS:', error)

      message.error(
        error?.response?.data?.message ||
          'Erro ao carregar jogos',
      )
    }
  }

  const loadPrizes = async (selectedGameId) => {
    if (!selectedGameId) {
      setPrizes([])
      setPrizeId(undefined)
      setConfigurations([])
      return
    }

    try {
      const list =
        await PrizeConfigurationsService.getPrizesByGame(
          selectedGameId,
        )

      const ordered = list
        .slice()
        .sort(
          (a, b) =>
            Number(a.position || 0) -
            Number(b.position || 0),
        )

      setPrizes(ordered)

      if (ordered.length) {
        setPrizeId((currentPrizeId) => {
          const exists = ordered.some(
            (prize) => prize.id === currentPrizeId,
          )

          return exists ? currentPrizeId : ordered[0].id
        })
      } else {
        setPrizeId(undefined)
        setConfigurations([])
      }
    } catch (error) {
      console.error('ERRO AO CARREGAR BRINDES:', error)

      message.error(
        error?.response?.data?.message ||
          'Erro ao carregar brindes',
      )
    }
  }

  const loadConfigurations = async (selectedPrizeId) => {
    if (!selectedPrizeId) {
      setConfigurations([])
      return
    }

    setLoading(true)

    try {
      const list =
        await PrizeConfigurationsService.getConfigurations(
          selectedPrizeId,
        )

      setConfigurations(
        list
          .slice()
          .sort(
            (a, b) =>
              new Date(a.date).getTime() -
              new Date(b.date).getTime(),
          ),
      )
    } catch (error) {
      console.error(
        'ERRO AO CARREGAR CONFIGURAÇÕES:',
        error,
      )

      message.error(
        error?.response?.data?.message ||
          'Erro ao carregar limites diários',
      )

      setConfigurations([])
    } finally {
      setLoading(false)
    }
  }

  const openCreate = () => {
    if (!prizeId) {
      message.error('Selecione um brinde')
      return
    }

    setEditingConfiguration(null)

    form.resetFields()

    form.setFieldsValue({
      date: dayjs(),
      enabled: true,
      dailyLimit: null,
      probabilityWeight:
        selectedPrize?.probabilityWeight || 1,
      notes: null,
    })

    setModalOpen(true)
  }

  const openEdit = (configuration) => {
    setEditingConfiguration(configuration)

    form.resetFields()

    form.setFieldsValue({
      date: dayjs(
  normalizeDateOnly(configuration.date),
),
      enabled: configuration.enabled ?? true,
      dailyLimit:
        configuration.dailyLimit ?? null,
      probabilityWeight:
        configuration.probabilityWeight ??
        selectedPrize?.probabilityWeight ??
        1,
      notes: configuration.notes || null,
    })

    setModalOpen(true)
  }

  const openDuplicate = (configuration) => {
    setEditingConfiguration(null)

    form.resetFields()

    form.setFieldsValue({
      date: dayjs(
  normalizeDateOnly(configuration.date),
).add(1, 'day'),
      enabled: configuration.enabled ?? true,
      dailyLimit:
        configuration.dailyLimit ?? null,
      probabilityWeight:
        configuration.probabilityWeight ??
        selectedPrize?.probabilityWeight ??
        1,
      notes: configuration.notes || null,
    })

    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingConfiguration(null)
    form.resetFields()
  }

  const handleSubmit = async (values) => {
    if (!prizeId) {
      message.error('Selecione um brinde')
      return
    }

    setSubmitting(true)

    try {
      const payload = {
        date: values.date.format('YYYY-MM-DD'),
        enabled: values.enabled ?? true,

        dailyLimit:
          values.dailyLimit === undefined ||
          values.dailyLimit === null
            ? null
            : Number(values.dailyLimit),

        probabilityWeight:
          values.probabilityWeight === undefined ||
          values.probabilityWeight === null
            ? null
            : Number(values.probabilityWeight),

        notes: values.notes || null,
      }

      if (editingConfiguration) {
  await PrizeConfigurationsService.updateConfiguration(
    editingConfiguration.id,
    payload,
  )

  message.success(
    'Limite diário atualizado com sucesso',
  )
} else {
  const existingConfiguration =
    configurations.find((configuration) => {
      return (
        normalizeDateOnly(configuration.date) ===
        payload.date
      )
    })

  if (existingConfiguration) {
    await PrizeConfigurationsService.updateConfiguration(
      existingConfiguration.id,
      payload,
    )

    message.success(
      'A configuração desta data foi atualizada.'
    )
  } else {
    await PrizeConfigurationsService.createConfiguration(
      prizeId,
      payload,
    )

    message.success(
      'Limite diário cadastrado com sucesso.'
    )
  }
}

      closeModal()
      await loadConfigurations(prizeId)
    } catch (error) {
      console.error(
        'ERRO AO SALVAR LIMITE DIÁRIO:',
        error,
      )

      message.error(
        error?.response?.data?.message ||
          'Erro ao salvar limite diário',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const removeConfiguration = async (configurationId) => {
    try {
      await PrizeConfigurationsService.deleteConfiguration(
        configurationId,
      )

      message.success('Configuração removida')

      await loadConfigurations(prizeId)
    } catch (error) {
      console.error(
        'ERRO AO EXCLUIR CONFIGURAÇÃO:',
        error,
      )

      message.error(
        error?.response?.data?.message ||
          'Erro ao excluir configuração',
      )
    }
  }

  const clearFilters = () => {
    setSearchPeriod(null)
    setStatusFilter(undefined)
  }

  useEffect(() => {
    loadGames()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadPrizes(gameId)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId])

  useEffect(() => {
    loadConfigurations(prizeId)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prizeId])

  const columns = [
    {
      title: 'Data',
      dataIndex: 'date',
      width: 150,
      sorter: (a, b) =>
        new Date(a.date).getTime() -
        new Date(b.date).getTime(),
      render: (value) => (
  <Space>
    <CalendarOutlined />

    <Text strong>
      {dayjs(normalizeDateOnly(value)).format(
        'DD/MM/YYYY',
      )}
    </Text>
  </Space>
),
    },
    {
      title: 'Brinde',
      width: 220,
      render: () => (
        <div>
          <Text strong>
            {selectedPrize?.name || '-'}
          </Text>

          <div>
            <Text type="secondary">
              Peso padrão:{' '}
              {selectedPrize?.probabilityWeight || 0}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Limite diário',
      dataIndex: 'dailyLimit',
      width: 150,
      sorter: (a, b) =>
        Number(a.dailyLimit || 0) -
        Number(b.dailyLimit || 0),
      render: (value) =>
        value === null || value === undefined ? (
          <Tag>Sem limite</Tag>
        ) : (
          <Tag color="blue">{value} brindes</Tag>
        ),
    },
    {
      title: 'Peso do dia',
      dataIndex: 'probabilityWeight',
      width: 130,
      render: (value) =>
        value === null || value === undefined ? (
          <Tag>
            Padrão {selectedPrize?.probabilityWeight || 0}
          </Tag>
        ) : (
          <Tag color="geekblue">{value}</Tag>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      width: 130,
      filters: [
        {
          text: 'Ativo',
          value: true,
        },
        {
          text: 'Inativo',
          value: false,
        },
      ],
      onFilter: (value, record) =>
        record.enabled === value,
      render: (enabled) =>
        enabled ? (
          <Tag
            color="green"
            icon={<CheckCircleOutlined />}
          >
            Ativo
          </Tag>
        ) : (
          <Tag color="red" icon={<StopOutlined />}>
            Inativo
          </Tag>
        ),
    },
    {
      title: 'Observação',
      dataIndex: 'notes',
      render: (value) => value || '-',
    },
    {
      title: 'Ações',
      width: 210,
      fixed: 'right',
      render: (_, configuration) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(configuration)}
          >
            Editar
          </Button>

          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => openDuplicate(configuration)}
          />

          <Popconfirm
            title="Excluir configuração?"
            description="O limite dessa data será removido."
            okText="Excluir"
            cancelText="Cancelar"
            okButtonProps={{
              danger: true,
            }}
            onConfirm={() =>
              removeConfiguration(configuration.id)
            }
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Space
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Limites Diários
          </Title>

          <p>
            Configure e confira a quantidade de cada
            brinde disponível em cada dia do evento.
          </p>
        </div>

        <Space>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            disabled={!prizeId}
            onClick={() =>
              loadConfigurations(prizeId)
            }
          >
            Atualizar
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!prizeId}
            onClick={openCreate}
          >
            Novo limite
          </Button>
        </Space>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Text strong>Jogo</Text>

            <Select
              style={{
                width: '100%',
                marginTop: 8,
              }}
              placeholder="Selecione o jogo"
              value={gameId}
              onChange={setGameId}
              showSearch
              optionFilterProp="children"
            >
              {games.map((game) => (
                <Select.Option
                  key={game.id}
                  value={game.id}
                >
                  {game.name}
                  {game.event?.name
                    ? ` - ${game.event.name}`
                    : ''}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} lg={12}>
            <Text strong>Brinde</Text>

            <Select
              style={{
                width: '100%',
                marginTop: 8,
              }}
              placeholder="Selecione o brinde"
              value={prizeId}
              onChange={setPrizeId}
              disabled={!gameId}
              showSearch
              optionFilterProp="children"
            >
              {prizes.map((prize) => (
                <Select.Option
                  key={prize.id}
                  value={prize.id}
                >
                  {prize.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      <Row
        gutter={[16, 16]}
        style={{ marginBottom: 16 }}
      >
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Datas configuradas"
              value={configurations.length}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Datas ativas"
              value={activeConfigurations}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Datas inativas"
              value={inactiveConfigurations}
              prefix={<StopOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Soma dos limites"
              value={totalConfiguredLimits}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Configurações cadastradas"
        extra={
          <Text type="secondary">
            {selectedGame?.name || 'Nenhum jogo'}
            {selectedPrize?.name
              ? ` — ${selectedPrize.name}`
              : ''}
          </Text>
        }
      >
        <Row
          gutter={[16, 16]}
          style={{ marginBottom: 16 }}
        >
          <Col xs={24} lg={10}>
            <DatePicker.RangePicker
              value={searchPeriod}
              onChange={setSearchPeriod}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              placeholder={[
                'Data inicial',
                'Data final',
              ]}
            />
          </Col>

          <Col xs={24} lg={7}>
            <Select
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filtrar por status"
              style={{ width: '100%' }}
            >
              <Select.Option value="ACTIVE">
                Ativos
              </Select.Option>

              <Select.Option value="INACTIVE">
                Inativos
              </Select.Option>
            </Select>
          </Col>

          <Col xs={24} lg={7}>
            <Button
              block
              onClick={clearFilters}
            >
              Limpar filtros
            </Button>
          </Col>
        </Row>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredConfigurations}
          columns={columns}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          scroll={{ x: 1150 }}
          locale={{
            emptyText:
              'Nenhum limite diário cadastrado para este brinde.',
          }}
        />

        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message="Cada brinde pode possuir um limite diferente em cada data. Uma configuração inativa impede que o brinde seja sorteado naquela data."
        />
      </Card>

      <Modal
        title={
          editingConfiguration
            ? 'Editar limite diário'
            : 'Novo limite diário'
        }
        visible={modalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        width={650}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
          message={
            selectedPrize
              ? `Configurando o brinde: ${selectedPrize.name}`
              : 'Selecione um brinde'
          }
        />

        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        >
          <Form.Item
            name="date"
            label="Data"
            rules={[
              {
                required: true,
                message: 'Selecione a data',
              },
            ]}
          >
            <DatePicker
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="enabled"
            label="Brinde habilitado nesta data"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="dailyLimit"
            label="Quantidade máxima no dia"
            extra="Deixe vazio para não aplicar limite diário."
          >
            <InputNumber
              min={0}
              precision={0}
              placeholder="Sem limite"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="probabilityWeight"
            label="Peso de probabilidade nesta data"
            extra={`Peso padrão do brinde: ${
              selectedPrize?.probabilityWeight || 0
            }`}
          >
            <InputNumber
              min={0}
              precision={0}
              placeholder="Utilizar peso padrão"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Observação"
          >
            <Input.TextArea
              rows={4}
              placeholder="Ex.: primeiro dia do evento, horário reduzido..."
            />
          </Form.Item>

          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
            >
              Salvar
            </Button>

            <Button onClick={closeModal}>
              Cancelar
            </Button>
          </Space>
        </Form>
      </Modal>
    </>
  )
}

export default DailyLimitsPage